/**
 * useActivateDeployment Hook
 *
 * React Query mutation for activating a deployment.
 * Activating a deployment makes it the current active deployment for the application.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { deploymentService } from '../../../api/deployment.service';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseActivateDeploymentOptions {
  tenantId: string;
  applicationId: string;
  onSuccess?: (data: DeploymentSanitized) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for activating a deployment
 */
export const useActivateDeployment = (
  options: UseActivateDeploymentOptions
): UseMutationResult<DeploymentSanitized, Error, string> => {
  const { tenantId, applicationId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<DeploymentSanitized, Error, string>({
    mutationFn: async (deploymentId: string) => {
      console.log('[useActivateDeployment] Activating deployment:', deploymentId);
      return await deploymentService.activateDeployment(tenantId, deploymentId);
    },
    onSuccess: (data) => {
      console.log('[useActivateDeployment] Deployment activated successfully:', data.deploymentId);
      // Invalidate both deployments and applications queries
      queryClient.invalidateQueries({ queryKey: ['deployments', tenantId, applicationId] });
      queryClient.invalidateQueries({ queryKey: ['applications', tenantId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useActivateDeployment] Failed to activate deployment:', getErrorMessage(error));
      onError?.(error);
    },
  });
};
